// line intercept math by Paul Bourke http://paulbourke.net/geometry/pointlineplane/
// Determine the intersection point of two line segments
// Return FALSE if the lines don't intersect
// export for testing
/**
 * Determines the intersection point of two line segments, or false if they don't intersect.
 *
 * @param x1 - The x-coordinate of the first point of the first line segment.
 * @param y1 - The y-coordinate of the first point of the first line segment.
 * @param x2 - The x-coordinate of the second point of the first line segment.
 * @param y2 - The y-coordinate of the second point of the first line segment.
 * @param x3 - The x-coordinate of the first point of the second line segment.
 * @param y3 - The y-coordinate of the first point of the second line segment.
 * @param x4 - The x-coordinate of the second point of the second line segment.
 * @param y4 - The y-coordinate of the second point of the second line segment.
 * @returns The [x, y] intersection point, or false if the segments don't intersect.
 */
export function getIntersection(x1, y1, x2, y2, x3, y3, x4, y4) {
    // Check if none of the lines are of length 0
    if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
        return false;
    }

    const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

    // Lines are parallel
    if (denominator === 0) {
        return false;
    }

    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

    // is the intersection along the segments
    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
        return false;
    }

    // Return a object with the x and y coordinates of the intersection
    const x = x1 + ua * (x2 - x1);
    const y = y1 + ua * (y2 - y1);

    return [+x.toFixed(3), +y.toFixed(3)];
}

/**
 * Checks whether a point exists in the given array of points.
 *
 * @param pointsArr - The array of [x, y] coordinate pairs to search.
 * @param point - The [x, y] coordinate pair to find.
 * @returns Whether the point exists in the array.
 */
export function containsPoint(pointsArr, point) {
    for (let i = 0; i < pointsArr.length; i += 1) {
        if (pointsArr[i][0] === point[0] && pointsArr[i][1] === point[1])
            return true;
    }
    return false;
}

// determine whether any of the line segments composing a polygon intersect
/**
 * Checks whether any line segments composing a polygon intersect each other.
 *
 * @param positions - The array of [x, y] vertices forming the polygon.
 * @returns Whether any segments of the polygon intersect each other.
 */
export function containsIntersections(positions) {
    for (let i = 0; i < positions.length - 1; i += 1) {
        const edgeToTestAgainstAllOthers = [
            positions[i][0],
            positions[i][1],
            positions[i + 1][0],
            positions[i + 1][1],
        ];

        for (let k = 0; k < positions.length - 1; k += 1) {
            const edge = [
                positions[k][0],
                positions[k][1],
                positions[k + 1][0],
                positions[k + 1][1],
            ];

            const result = getIntersection(
                ...edgeToTestAgainstAllOthers,
                ...edge,
            );

            if (result && !containsPoint(positions, result)) {
                return true;
            }
        }
    }

    return false;
}

/**
 * Determines whether a point is inside a polygon using ray casting.
 *
 * @param position - The [x, y] point to test.
 * @param polygon - The array of [x, y] vertices forming the polygon.
 * @returns Whether the point is inside the polygon.
 */
export function polygonContainsPoint(position, polygon) {
    const [x, y] = position;

    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][0];
        const yi = polygon[i][1];
        const xj = polygon[j][0];
        const yj = polygon[j][1];

        const intersect =
            yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

        if (intersect) inside = !inside;
    }

    return inside;
}

/**
 * Converts an array of Leaflet LatLng objects to an array of [lat, lng] number pairs.
 *
 * @param poly - The array of Leaflet LatLng objects to convert.
 * @returns The array of [lat, lng] number pairs.
 */
export function latLngToArray(poly) {
    return poly.map(({ lat, lng }) => [+lat.toFixed(3), +lng.toFixed(3)]);
}
